<script lang="ts">
    import { chart } from "./lib/apex-chart";
    import { afterUpdate } from 'svelte';

    import type { Pattern, Coordinate } from './pattern';
    import type { CalculatedInfo, Sample } from './samples';
    import { PauseStep } from './pattern/step';

    const svgNS = "http://www.w3.org/2000/svg";

    // props
    export let pattern: Pattern | null = null;
    export let calculatedInfo: CalculatedInfo | null = null;
    export let isPlaying: boolean = false;

    let _pattern: Pattern;

    let apexGridGroup: SVGElement, positionMarkerElement: SVGElement, positionMarkerAnimate;
    

    // const onChartUpdate = (chartContext, config) => {
    //     updatePositionMarker();
    // };
    // const onChartMounted = (chartContext, config) => {
    //     //updatePositionMarker();
    // };

    const calculateDataSeries = (pattern: Pattern): object => {
        // one "line" per step (so we can also have gaps)
        return pattern.steps.map((step, index) => {
            return {
                name: "skratch-record-step-" + index,
                data: [
                    [step.startX, step.startY],
                    [step.endX, step.endY],
                ],
            };
        });
    };
    const calculateFaderCutMarkers = (pattern: Pattern): object[] => {
        let markers: object[] = [];
        pattern.steps.forEach((step, index) => {
            if (step.startClosed) {
                markers.push({
                   seriesIndex: index,
                   dataPointIndex: 0,
                   fillColor: faderClickColor,
                   strokeColor: faderClickColor,
                   size: 5
               });
            }
            if (step.endClosed) {
                markers.push({
                   seriesIndex: index,
                   dataPointIndex: 1,
                   fillColor: faderClickColor,
                   strokeColor: faderClickColor,
                   size: 5
               });
            }
        });
        return markers;
    };

    const calculateClickCoordinates = (ev: MouseEvent): Coordinate | null => {
        const apexCanvas: HTMLElement = document.getElementsByClassName('apexcharts-canvas')[0] as HTMLElement;
        apexGridGroup = document.getElementsByClassName('apexcharts-gridlines-horizontal')[0] as SVGElement;

        const canvasDomRect: DOMRect = apexCanvas.getBoundingClientRect();
        const gridDomRect: DOMRect = apexGridGroup.getBoundingClientRect();
        
        const paddings = {
            top: gridDomRect.top - canvasDomRect.top,
            left: gridDomRect.left - canvasDomRect.left,
        }
        
        const gridX = ev.offsetX - paddings.left;
        const gridY = ev.offsetY - paddings.top;

        const isXInGrid = gridX >= 0 && gridX <= gridDomRect.width;
        const isYInGrid = gridY >= 0 && gridY <= gridDomRect.height;

        if (isXInGrid && isYInGrid) {
            const valueX = pattern.beats * gridX / gridDomRect.width;
            const valueY = 1 - gridY / gridDomRect.height;

            return {x: valueX, y: valueY};
        }
        
        return null;
    };

    let lineColor: string = '#0570c2';
    let faderClickColor: string = '#064b7f';

    let options = {
        chart: {
            type: "line",
            width: '100%',
            height: '100%',
            animations: {
                enabled: false,
                easing: 'linear',
                speed: 100,
                animateGradually: {
                    enabled: false,
                    delay: 1500
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 300
                }
            },
            // events: {
            //     // mounted: onChartMounted,
            //     // updated: onChartMounted, 
            //     dataPointSelection: function(e, chart, opts) {
            //         console.log('dataPointSelection', e, opts)
            //     },
            //     click: function(event: MouseEvent, chartContext, { seriesIndex, dataPointIndex, config}) {
            //         //console.log('KILKKKK', event, chartContext, config, 'Series:', seriesIndex, dataPointIndex);
            //         const clickCoords = calculateClickCoordinates(event);
            //         if (clickCoords) {
            //             console.log('GRID was clicked at', clickCoords);
            //         }
            //     },
            // },
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        series: [],
        xaxis: {},
        yaxis: {},
        colors: [lineColor],
        grid: {
            show: true,
            borderColor: '#8b9699',
            strokeDashArray: 2,
            position: 'back',
            xaxis: {
                lines: {
                    show: true
                }
            },
            yaxis: {
                lines: {
                    show: true
                }
            },
            column: {
                colors: [ // for a 4/4 signature
                    '#ffffff', '#ffffff', '#ffffff', '#ffffff',
                    '#ddddee', '#ddddee', '#ddddee', '#ddddee',
                ],
                //opacity: 0.5
            },  
        },
        markers: {
            size: 1, // has to be t least 1 to render them
            colors: [lineColor],
            fillColor: lineColor,
            strokeColor: lineColor,
        },
        legend: { show: false },
        tooltip: { 
            enabled: false,
            intersect: true,
            shared: false,
        },
        update: { /* used for updates, see lib/apex-chart */ },
    };

    const updateChartOptions = (
        calculatedInfo: CalculatedInfo | null, 
        pattern: Pattern | null
    ) => {
        if (!pattern) {
            return;
        }

        options.update = {
            series: calculateDataSeries(pattern),
            xaxis: {
                min: 0,
                tickAmount: pattern.beats,
                max: pattern.beats,
                labels: {
                    formatter: function (value: number) {
                        if (value >= pattern.beats) {
                            return;
                        }
                        return (value % 4) + 1;
                    }
                }
            },
            yaxis: {
                type: 'percentage',
                min: 0,
                max: 1,
                tickAmount: 1, //sample.segments,
                labels: {
                    formatter: function (value) {
                        if (value === 0) { return 'start'; }
                        if (value === 1) { return 'end'; }
                        return ''; //value*100 + '%';
                    }
                }
            },
            markers: {
                discrete: calculateFaderCutMarkers(pattern),
            },
        };
    };

    const updatePositionMarker = () => {
        apexGridGroup = document.getElementsByClassName('apexcharts-gridlines-horizontal')[0] as SVGElement;
        if (!apexGridGroup || !calculatedInfo) {
            return;
        }
        
        const boxHeight: number = apexGridGroup.getBBox().height;
        const boxWidth: number = apexGridGroup.getBBox().width;

        positionMarkerElement = document.getElementById('positionMarker') as unknown as SVGElement;
        const hasPositionMarkerElement: boolean = !!positionMarkerElement;
        
        if (!isPlaying) {
            if (hasPositionMarkerElement) {
                positionMarkerElement.remove();
            }

            return;
        }

        if (!positionMarkerElement) {
            positionMarkerElement = document.createElementNS(svgNS, 'rect');
        }
        positionMarkerElement.setAttributeNS(null, 'id', 'positionMarker');
        positionMarkerElement.setAttributeNS(null, 'class', 'positionMarker');
        positionMarkerElement.setAttributeNS(null, 'width', '2');
        positionMarkerElement.setAttributeNS(null, 'height', boxHeight);
        positionMarkerElement.setAttributeNS(null, 'x', '0');
        positionMarkerElement.setAttributeNS(null, 'y', '0');

        positionMarkerAnimate = document.getElementById('positionMarkerAnimate');
        const hasPositionMarkerAnimate: boolean = !!positionMarkerAnimate;
        if (!hasPositionMarkerAnimate) {
            positionMarkerAnimate = document.createElementNS(svgNS, 'animate');
        }
        positionMarkerAnimate.setAttributeNS(null, 'id', 'positionMarkerAnimate');
        positionMarkerAnimate.setAttributeNS(null, 'attributeType', 'XML');
        positionMarkerAnimate.setAttributeNS(null, 'attributeName', 'width');
        positionMarkerAnimate.setAttributeNS(null, 'from', '1');
        positionMarkerAnimate.setAttributeNS(null, 'to', boxWidth);
        positionMarkerAnimate.setAttributeNS(null, 'dur', calculatedInfo.durationForPattern + 's');
        positionMarkerAnimate.setAttributeNS(null, 'repeatCount', 'indefinite');
        positionMarkerAnimate.setAttributeNS(null, 'restart', 'always');


        if (!hasPositionMarkerAnimate) {
            positionMarkerElement.appendChild(positionMarkerAnimate);
        }

        if (!hasPositionMarkerElement) {
            apexGridGroup.appendChild(positionMarkerElement);
        }
        
        positionMarkerAnimate.beginElement();
    };


    $: {
        if (pattern) {
            _pattern = pattern.clone();
            _pattern.steps = _pattern.steps.filter((step) => {
                return ! (step instanceof PauseStep);
            });

            updateChartOptions(calculatedInfo, _pattern);
        }       
    };

    afterUpdate(() => {
        updatePositionMarker();
    });

</script>

<style>
#ttmChartBox {
    height: 50vh;
}
:global(#positionMarker) {
    fill: #ffc107;
    opacity: 0.4;
    z-index: 9000;
}
</style>

<div id="ttmChartBox">
    <div id="ttmChart" use:chart={options} class="text-center"></div>
</div>
